import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { refreshBalanceSheetValidation } from '../../../../Components/BalanceSheet/BalanceSheetHeader';
import axios from 'axios';
import { API_URL } from '../../../../Constant';
import { showLoading } from 'react-global-loading';
import { toast, ToastContainer } from 'react-toastify';
import messages from '../../../../assets/Messages.json';
import 'react-toastify/dist/ReactToastify.css';
import HeaderInfo from '../../../../Components/Common/HeaderInfo';
import Remarks from './Remarks';
import Remarks__list from './Remarks__list';
import { filterRemarksList, isNumeric } from '../../../../Custom/Custom';

// API configuration - using dynamic token with multiple fallbacks
const getAuthConfig = () => {
  let token = localStorage.getItem('token') ||
    localStorage.getItem('jwt') ||
    localStorage.getItem('accessToken') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('userToken');

  if (!token) {
    token = sessionStorage.getItem('token') ||
      sessionStorage.getItem('jwt') ||
      sessionStorage.getItem('accessToken');
  }

  if (!token) {
    console.error('No token found in localStorage or sessionStorage');
    return null;
  }

  return {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json',
      'Cache-Control': 'no-cache'
    }
  };
};

function Part2() {
  const navigate = useNavigate();

  // State for header data
  const [headerData, setHeaderData] = useState({
    despatchSlNo: '',
    permanentSlNo: '',
    companyName: '',
    dslNo: '',
    surveyYear: ''
  });

  // State for table data
  const [tableData, setTableData] = useState([]);

  // State for Remarks section
  const [remarksData, setRemarksData] = useState({
    remarksBlock: '',
    remarksItems: '',
    remarks: ''
  });
  const [remarksList, setRemarksList] = useState([]);


  // Use ref to track if data has been loaded to prevent multiple API calls
  const dataLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);
  const apiCallMadeRef = useRef(false);

  // Memoize the fetch function to prevent recreation
  const fetchHeaderData = useCallback(async () => {
    // Prevent multiple API calls using refs
    if (dataLoadedRef.current || isLoadingRef.current || apiCallMadeRef.current) {
      console.log('Data already loaded, loading in progress, or API call already made - skipping');
      return;
    }

    try {
      const balanceSheetData = localStorage.getItem('balanceSheetFormData');

      if (balanceSheetData) {
        const parsedData = JSON.parse(balanceSheetData);

        const headerInfo = {
          despatchSlNo: parsedData.dslNo || '',
          permanentSlNo: parsedData.pslNo || '',
          companyName: parsedData.nameUnit || '',
          dslNo: parsedData.dslNo || '',
          surveyYear: parsedData.year || ''
        };

        setHeaderData(headerInfo);
      } else {
        console.log('No balanceSheet data found in localStorage');
        setHeaderData({
          despatchSlNo: 'DSL No. not set',
          permanentSlNo: 'PSL No. not set',
          companyName: 'Company name not set',
          dslNo: '',
          surveyYear: ''
        });
      }
    } catch (error) {
      console.error('Error fetching data from localStorage:', error);
      setHeaderData({
        despatchSlNo: 'Error loading data',
        permanentSlNo: 'Error loading data',
        companyName: 'Error loading data',
        dslNo: '',
        surveyYear: ''
      });
    }
  }, []);

  // Fetch header data from localStorage when component mounts
  useEffect(() => {
    fetchHeaderData();
  }, [fetchHeaderData]);

  // Generate financial year months (April to March) when survey year is available
  useEffect(() => {
    if (headerData.surveyYear && tableData.length === 0) {
      // Extract starting year from survey year (e.g., "2024-25" -> 2024)
      const yearMatch = headerData.surveyYear.match(/^(\d{4})/);
      const startYear = yearMatch ? parseInt(yearMatch[1]) : new Date().getFullYear();
      const endYear = startYear + 1;

      // Financial year months: April to March
      const monthNames = [
        { name: 'April', year: startYear },
        { name: 'May', year: startYear },
        { name: 'June', year: startYear },
        { name: 'July', year: startYear },
        { name: 'August', year: startYear },
        { name: 'September', year: startYear },
        { name: 'October', year: startYear },
        { name: 'November', year: startYear },
        { name: 'December', year: startYear },
        { name: 'January', year: endYear },
        { name: 'February', year: endYear },
        { name: 'March', year: endYear }
      ];

      // Initialize table data with 12 months
      const initialTableData = monthNames.map((month, index) => ({
        slNo: index + 1,
        month: `${month.name}-${month.year}`,
        scheduleWorkingDays: '',
        mandaysWorked: '',
        mandaysLost: '',
        workersFirstDay: '',
        workersLastDay: '',
        accessionDuringMonth: '',
        separationDeathRetirement: '',
        separationOtherCauses: ''
      }));

      setTableData(initialTableData);
    }
  }, [headerData.surveyYear, tableData.length]);

  // Get selected Item Descriptions from table for Remarks Items dropdown (months)
  const selectedItemDescriptions = useMemo(() => {
    return tableData.map(row => row.month || '').filter(month => month !== '');
  }, [tableData]);

  // Handle input change with numeric validation
  const handleInputChange = (index, field, value) => {
    // Numeric validation for all numeric fields
    const numericFields = [
      'scheduleWorkingDays',
      'mandaysWorked',
      'mandaysLost',
      'workersFirstDay',
      'workersLastDay',
      'accessionDuringMonth',
      'separationDeathRetirement',
      'separationOtherCauses'
    ];

    if (numericFields.includes(field)) {
      // Allow empty string or numeric values (integers only)
      if (value === '' || /^\d*$/.test(value)) {
        // Limit to 10 digits
        if (value.length > 10) {
          toast.error('Maximum 10 digits allowed', messages.COLOR_RED);
          return;
        }
        const updatedData = [...tableData];
        updatedData[index][field] = value;
        setTableData(updatedData);
      } else {
        toast.error('Please enter numeric value only', messages.COLOR_RED);
      }
    } else {
      const updatedData = [...tableData];
      updatedData[index][field] = value;
      setTableData(updatedData);
    }
  };

  // Handle reset function
  const handleReset = () => {
    const updatedData = tableData.map(row => ({
      ...row,
      scheduleWorkingDays: '',
      mandaysWorked: '',
      mandaysLost: '',
      workersFirstDay: '',
      workersLastDay: '',
      accessionDuringMonth: '',
      separationDeathRetirement: '',
      separationOtherCauses: ''
    }));
    setTableData(updatedData);
    setRemarksData({
      remarksBlock: '',
      remarksItems: '',
      remarks: ''
    });
  };

  // Function to save data to API
  const handleSaveToAPI = async () => {
    if (!headerData.dslNo || headerData.dslNo === 'DSL No. not set') {
      toast.error('Please ensure DSL No. is available', messages.COLOR_RED);
      return false;
    }

    showLoading(true);

    try {
      const currentConfig = getAuthConfig();
      if (!currentConfig) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        showLoading(false);
        return false;
      }

      // Extract year only from survey year (e.g., "2024-25" -> "2024")
      const yearOnly = headerData.surveyYear ? headerData.surveyYear.split('-')[0] : headerData.surveyYear || '2024';

      // Prepare data for API
      const dataToSave = [];
      tableData.forEach((row) => {
        if (row.month && row.month.trim() !== '') {
          // Extract month name and year from "April-2024" format
          const monthMatch = row.month.match(/^(\w+)-(\d{4})$/);
          const monthName = monthMatch ? monthMatch[1] : '';
          const year = monthMatch ? monthMatch[2] : yearOnly;

          dataToSave.push({
            dsl_no: headerData.dslNo,
            survey_year: year,
            month_name: monthName,
            schedule_working_days: row.scheduleWorkingDays || '',
            mandays_worked: row.mandaysWorked || '',
            mandays_lost: row.mandaysLost || '',
            workers_first_day: row.workersFirstDay || '',
            workers_last_day: row.workersLastDay || '',
            accession_during_month: row.accessionDuringMonth || '',
            separation_death_retirement: row.separationDeathRetirement || '',
            separation_other_causes: row.separationOtherCauses || ''
          });
        }
      });

      if (dataToSave.length === 0) {
        toast.warning('No data to save. Please fill at least one row.', messages.COLOR_RED);
        showLoading(false);
        return false;
      }

      // Prepare remarks array
      const remarksArray = [];
      if (remarksData.remarksBlock || remarksData.remarksItems || remarksData.remarks) {
        remarksArray.push({
          dslNo: headerData.dslNo.toString(),
          surveyYear: yearOnly,
          block: remarksData.remarksBlock || '',
          columnsName: remarksData.remarksItems || '',
          items: remarksData.remarksBlock || '',
          remarks: remarksData.remarks || ''
        });
      }

      // Structure payload with data and remarks arrays
      const finalPayload = {
        data: dataToSave,
        remarks: remarksArray
      };

      console.log('Saving data to API:', finalPayload);

      // TODO: Update API endpoint when available
      const response = await axios.post(`${API_URL}api/part2/insertPart2Data`, finalPayload, currentConfig);

      if (response.data && response.data.status) {
        toast.success('Data saved successfully!', messages.COLOR_RED);
        // Refresh balance sheet validation to update header colors
        if (headerData.dslNo && headerData.surveyYear) {
          await refreshBalanceSheetValidation(headerData.dslNo, headerData.surveyYear);
        }
        return true;
      } else {
        toast.error('Failed to save data. Please check the data and try again.', messages.COLOR_RED);
        return false;
      }

    } catch (error) {
      console.error('API Error Details:', error);
      console.error('Error Response:', error.response);
      console.error('Error Status:', error.response?.status);
      console.error('Error Data:', error.response?.data);

      if (error.response?.status === 403) {
        toast.error('Access denied (403). Please check your permissions or contact administrator.', messages.COLOR_RED);
      } else if (error.response?.status === 401) {
        toast.error('Unauthorized (401). Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 404) {
        toast.error('API endpoint not found (404). Please check the URL.', messages.COLOR_RED);
      } else if (error.response?.status >= 500) {
        toast.error('Server error. Please try again later.', messages.COLOR_RED);
      } else {
        toast.error(`Error ${error.response?.status || 'Unknown'}: ${error.message || 'An error occurred'}`, messages.COLOR_RED);
      }
      return false;
    } finally {
      showLoading(false);
    }
  };

  return (
    <div className="container-fluid py-3">
      {/* Header Section - Direct Display */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col-md-12 mb-3">
              <h4 className="page_heading mb-3 pb-2 border-bottom">
                ASI Schedule 2024-25 <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>CONFIDENTIAL <i className="fa fa-lock me-2" style={{ fontSize: '20px', color: '#1976d2' }}></i></span>
              </h4>
              <h5 className="mb-0 pb-3 border-bottom"><strong>Block 2 of Part 2 </strong> <span style={{ color: '#1976d2', fontWeight: 'bold' }}>Labour Bereau</span></h5>
            </div>
            <div className="row">
              <div className="col-md-8">
                <div className="row g-3">
                  <div className="col-md-4">
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '6px' }}>
                      <strong>DSL No.:</strong>
                    </div>
                    <div style={{ fontSize: '14px', color: '#333' }}>
                      {headerData.despatchSlNo}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '6px' }}>
                      <strong>PSL No.:</strong>
                    </div>
                    <div style={{ fontSize: '14px', color: '#333' }}>
                      {headerData.permanentSlNo}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '6px' }}>
                      <strong>Name of Company:</strong>
                    </div>
                    <div style={{ fontSize: '14px', color: '#333' }}>
                      {headerData.companyName}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row">
            <div className="col-12">
              <div className="table-responsive">
                <table className="table table-bordered mb-0">
                  <thead>
                    <tr>
                      <th rowSpan="2">SL No.</th>
                      <th rowSpan="2">Month</th>
                      <th rowSpan="2">Schedule working days for workers</th>
                      <th rowSpan="2">No. of Mandays Worked</th>
                      <th rowSpan="2">No. of mandays lost due to absence</th>
                      <th colSpan="2">No. of Workers in employment on</th>
                      <th rowSpan="2">Accession During the month</th>
                      <th colSpan="2">Separation during the month due to</th>
                    </tr>
                    <tr>
                      <th>First day of Month</th>
                      <th>Last day of Month</th>
                      <th>Death or Retirement</th>
                      <th>Other Causes</th>
                    </tr>
                    <tr>
                      <th style={{ textAlign: 'center' }}></th>
                      <th style={{ textAlign: 'center' }}>(1)</th>
                      <th style={{ textAlign: 'center' }}>(2)</th>
                      <th style={{ textAlign: 'center' }}>(3)</th>
                      <th style={{ textAlign: 'center' }}>(4)</th>
                      <th style={{ textAlign: 'center' }}>(5)</th>
                      <th style={{ textAlign: 'center' }}>(6)</th>
                      <th style={{ textAlign: 'center' }}>(7)</th>
                      <th style={{ textAlign: 'center' }}>(8)</th>
                      <th style={{ textAlign: 'center' }}>(9)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.length > 0 ? (
                      tableData.map((row, index) => {
                        return (
                          <tr key={index}>
                            <td style={{ textAlign: 'center' }}>{row.slNo || index + 1}</td>
                            <td style={{ fontWeight: '500' }}>{row.month || ''}</td>
                            <td style={{ textAlign: 'center' }}>
                              <input
                                type="text"
                                className="form-control"
                                value={row.scheduleWorkingDays || ''}
                                onChange={(e) => handleInputChange(index, 'scheduleWorkingDays', e.target.value)}
                                style={{ width: '100%', textAlign: 'center' }}
                              />
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <input
                                type="text"
                                className="form-control"
                                value={row.mandaysWorked || ''}
                                onChange={(e) => handleInputChange(index, 'mandaysWorked', e.target.value)}
                                style={{ width: '100%', textAlign: 'center' }}
                              />
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <input
                                type="text"
                                className="form-control"
                                value={row.mandaysLost || ''}
                                onChange={(e) => handleInputChange(index, 'mandaysLost', e.target.value)}
                                style={{ width: '100%', textAlign: 'center' }}
                              />
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <input
                                type="text"
                                className="form-control"
                                value={row.workersFirstDay || ''}
                                onChange={(e) => handleInputChange(index, 'workersFirstDay', e.target.value)}
                                style={{ width: '100%', textAlign: 'center' }}
                              />
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <input
                                type="text"
                                className="form-control"
                                value={row.workersLastDay || ''}
                                onChange={(e) => handleInputChange(index, 'workersLastDay', e.target.value)}
                                style={{ width: '100%', textAlign: 'center' }}
                              />
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <input
                                type="text"
                                className="form-control"
                                value={row.accessionDuringMonth || ''}
                                onChange={(e) => handleInputChange(index, 'accessionDuringMonth', e.target.value)}
                                style={{ width: '100%', textAlign: 'center' }}
                              />
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <input
                                type="text"
                                className="form-control"
                                value={row.separationDeathRetirement || ''}
                                onChange={(e) => handleInputChange(index, 'separationDeathRetirement', e.target.value)}
                                style={{ width: '100%', textAlign: 'center' }}
                              />
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <input
                                type="text"
                                className="form-control"
                                value={row.separationOtherCauses || ''}
                                onChange={(e) => handleInputChange(index, 'separationOtherCauses', e.target.value)}
                                style={{ width: '100%', textAlign: 'center' }}
                              />
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="10" style={{ textAlign: 'center', padding: '20px' }}>
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Remarks Section */}
      <div className='row'>
        <div className='col-md-6 remarks-section'>
          <Remarks
            remarksData={remarksData}
            setRemarksData={setRemarksData}
            blockItemData={[]}
            filteredBlockItems={[]}
            isLoadingBlockItems={false}
            debouncedBlockItemSearch={() => { }}
            loadMoreBlockItemData={() => { }}
            hasMoreBlockItems={false}
            isLoadingMoreBlockItems={false}
            selectedItemsFromTable={selectedItemDescriptions}
            remarksList={remarksList}
            availableColumns={[1, 2, 3, 4, 5, 6, 7, 8, 9]}
          />
        </div>
        <div className='col-md-6'>
            <div className='card'>
              <div className='card-body'>
                <h6 className='mb-3' style={{ fontWeight: 'bold', color: '#f56565' }}><i className='fa fa-exclamation-triangle me-2'></i>Note</h6>
                <ul className='list-styled' style={{ fontSize: '14px', color: '#f56565' }}>
                  <li>For the 'Others' item, Columns 3 and Blocks are also Mandatory</li>
                </ul>
              </div>
            </div>
          </div>
      </div>

      {/* Action Buttons */}
      <div className="row">
        <div className="col-12">
          <hr />
        </div>
        <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
          <button
            type="button"
            className="btn btn_orange mx-1"
          >
            Print
            <i className="fa fa-print ms-1"></i>
          </button>
          <button
            type="button"
            className="btn btn_orange mx-1"
            onClick={handleSaveToAPI}
          >
            Save
          </button>
          <button
            type="reset"
            className="btn btn_orange mx-1"
            onClick={handleReset}
          >
            Reset
          </button>
          <button
            type="button"
            className="btn btn_orange mx-1"
            onClick={() => navigate('/asiScheduleMainMenu')}
          >
            Cancel
          </button>
        </div>
        <div className="col-md-4 text-center text-md-end">
          <button
            type="button"
            className="btn btn_orange mx-1"
            onClick={() => navigate('/ict-usage-energy-conservation')}
          >
            <i className="fa fa-angle-left me-1"></i>
            Previous
          </button>
          <button
            type="button"
            className="btn btn_orange mx-1"
            onClick={async () => {
              // Save data first, then navigate only if successful
              const saveSuccess = await handleSaveToAPI();
              if (saveSuccess) {
                navigate('/final-checklist');
              }
            }}
          >
            Next
            <i className="fa fa-angle-right ms-1"></i>
          </button>
        </div>
      </div>

      {/* Remarks List Section */}
      {(() => {
        const filteredRemarksList = filterRemarksList(remarksList, remarksData);
        return <div className=''><Remarks__list remarksData={filteredRemarksList} /></div>;
      })()}

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />
    </div>
  );
}

export default Part2;
